import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Carregando from "../componentes/Carregando";
import {
  AvisoEducacional,
  CAMPO_SIM,
  Caixa,
  Campo,
  Cartao,
  ExplicacaoDaEtapa,
  Navegar,
  CabineDaRotina,
  PopupDaEtapa,
  dicasDesligadas,
} from "../componentes/SimuladorMeiUi";
import { ETAPAS_DAS } from "../componentes/SimuladorMeiTextos";
import { filtrar, listarOcupacoes, type Ocupacao } from "../lib/ocupacoes";
import { cnpjValido, mascararCnpj, mascararData } from "../lib/mascaras";
import {
  MESES,
  anosDoDas,
  apuravel,
  compor,
  dataBr,
  dataHoraBr,
  vencimentoDo,
  type AtividadeEscolhida,
  type Composicao,
} from "../lib/dasMei";
import {
  gerarLinhaDigitavel,
  gerarNumeroApuracao,
  gerarNumeroDas,
  lerParametrosDas,
  type ParametrosDas,
} from "../lib/simuladorMei";
import { htmlDoDas, type PaginaDas } from "../lib/documentosMei";

// A apuração do DAS: o PGMEI, do CNPJ ao boleto.
//
// ---------------------------------------------------------------------------
// A atividade é do ALUNO, e é ela que decide o tributo
// ---------------------------------------------------------------------------
// O app de origem entrava nesta tela com uma empresa fictícia cravada no código
// ("Empresa Fictícia ME", comércio varejista + um serviço) e decidia ICMS e ISS
// por `startsWith` na descrição do CNAE. O aluno não escolhia nada e não via a
// ligação entre a atividade e o tributo — que é justamente o que esta rotina
// ensina.
//
// Aqui ele escolhe a atividade da lista do hub, e a composição sai dos FATOS
// gravados nela (`mei_ocupacoes.icms`, `.iss`). O quadro "de onde vem cada
// valor" mostra a ligação com nome e sobrenome.
export default function SimuladorDas() {
  const [params] = useSearchParams();
  const cnpjDaUrl = params.get("cnpj") ?? "";

  const [parametros, setParametros] = useState<ParametrosDas | null>(null);
  const [lista, setLista] = useState<Ocupacao[] | null>(null);
  const [erro, setErro] = useState(false);

  const [etapa, setEtapa] = useState<0 | 1 | 2 | 3>(0);
  const [cnpj, setCnpj] = useState(() => (cnpjDaUrl ? mascararCnpj(cnpjDaUrl) : ""));
  const [erroCnpj, setErroCnpj] = useState<string | null>(null);
  const [nome, setNome] = useState("");

  const [caminhoneiro, setCaminhoneiro] = useState(false);
  const [atividades, setAtividades] = useState<AtividadeEscolhida[]>([]);
  const [busca, setBusca] = useState("");

  const agora = useMemo(() => new Date(), []);
  const anos = useMemo(() => anosDoDas(agora), [agora]);
  const [ano, setAno] = useState(() => agora.getFullYear());
  const [mesAbertura, setMesAbertura] = useState(1);
  const [anoAbertura, setAnoAbertura] = useState(() => agora.getFullYear());
  const [marcados, setMarcados] = useState<Record<number, boolean>>({});
  const [dataPagamento, setDataPagamento] = useState(() => dataBr(agora));

  const [gerados, setGerados] = useState<PaginaDas[]>([]);

  const [semDicas] = useState(dicasDesligadas);
  const [popup, setPopup] = useState<number | null>(null);
  const [jaMostradas, setJaMostradas] = useState<number[]>([]);

  useEffect(() => {
    if (semDicas) return;
    if (jaMostradas.includes(etapa)) return;
    setPopup(etapa);
    setJaMostradas((v) => [...v, etapa]);
  }, [etapa, semDicas, jaMostradas]);

  useEffect(() => {
    void (async () => {
      const [p, o] = await Promise.all([lerParametrosDas(), listarOcupacoes()]);
      if (p === null || o === null) setErro(true);
      else {
        setParametros(p);
        setLista(o);
      }
    })();
  }, []);

  if (erro) {
    return (
      <Moldura>
        <p className="surface p-6 text-center text-sm leading-relaxed text-cinza">
          Não conseguimos carregar os valores do DAS e a lista de atividades agora. Recarregue a
          página; se continuar, fale com o suporte.
        </p>
      </Moldura>
    );
  }
  if (!parametros || !lista) return <Carregando />;

  const composicao = compor(parametros, atividades, caminhoneiro);

  const periodos = MESES.map((mes, i) => {
    const numero = i + 1;
    const podeApurar = apuravel(ano, numero, anoAbertura, mesAbertura);
    return {
      indice: i,
      mes,
      podeApurar,
      valor: podeApurar ? composicao.total : 0,
      vencimento: dataBr(vencimentoDo(ano, numero)),
    };
  });

  const escolhidos = periodos.filter((p) => marcados[p.indice] && p.podeApurar);

  const gerar = () => {
    if (escolhidos.length === 0) return;
    setGerados(
      escolhidos.map((p) => ({
        periodoApuracao: `${p.mes}/${ano}`,
        periodoCurto: `${String(p.indice + 1).padStart(2, "0")}/${ano}`,
        vencimento: p.vencimento,
        numeroDocumento: gerarNumeroDas(),
        composicao: {
          inss: composicao.inss,
          icms: composicao.icms,
          iss: composicao.iss,
          total: composicao.total,
        },
      })),
    );
    setEtapa(3);
  };

  const imprimir = () => {
    const janela = window.open("", "_blank");
    if (!janela) {
      window.alert("Libere as janelas pop-up deste site para visualizar o DAS.");
      return;
    }
    janela.document.write(
      htmlDoDas({
        cnpj,
        razaoSocial: nome.trim() || "EMPRESA FICTÍCIA MEI",
        cpf: "",
        // Esta tela não pede endereço, e o DAS real estampa o município nas
        // linhas de ICMS e ISS. Um nome de cidade inventado aqui leria como dado
        // real num documento que imita boleto — então o campo DIZ que não foi
        // informado, em vez de escolher uma cidade por conta própria.
        municipio: "Município não informado",
        uf: "--",
        dataPagamento,
        dataEmissao: dataHoraBr(new Date()),
        linhaDigitavel: gerarLinhaDigitavel(),
        paginas: gerados,
      }),
    );
    janela.document.close();
  };

  const texto = ETAPAS_DAS[etapa];

  return (
    <Moldura
      passos={etapa > 0 ? ["Acesso", "Avisos", "Períodos", "DAS gerado"] : undefined}
      atual={etapa + 1}
      // O herói do DAS é o VALOR do DAS, vivo — o número que a rotina inteira
      // existe para produzir, mudando enquanto o aluno escolhe as atividades.
      //
      // Sem atividade escolhida ele APARECE assim mesmo, com o apoio dizendo que
      // é só o INSS e o que falta. A primeira versão o escondia nesse caso, para
      // não passar o INSS sozinho por DAS — mas o quadro "de onde vem cada
      // valor", logo abaixo, já mostra "Total do DAS — R$ 81,05" com essa mesma
      // ressalva. Esconder o herói fazia a cabine contradizer a tela, e tirava
      // justamente o número que muda ao vivo quando a atividade é escolhida —
      // que é a coisa que esta rotina ensina.
      heroi={
        etapa > 0
          ? {
              rotulo: "DAS por mês",
              valor: reais(composicao.total),
              apoio: composicao.semAtividade
                ? "Só o INSS — escolha a atividade para ver ICMS e ISS"
                : `INSS ${reais(composicao.inss)}${composicao.icms > 0 ? ` · ICMS ${reais(composicao.icms)}` : ""}${composicao.iss > 0 ? ` · ISS ${reais(composicao.iss)}` : ""}`,
            }
          : null
      }
      // O CNPJ e o nome ficam na cabine a partir da segunda etapa: são a
      // identidade do que está sendo apurado, e antes viviam num card branco
      // separado logo abaixo do título — um cartão inteiro para dizer duas
      // linhas.
      identidade={
        etapa > 0
          ? [
              ["CNPJ", cnpj || "—"],
              ["Nome empresarial", nome.trim() || "não informado"],
            ]
          : undefined
      }
    >

      {etapa === 0 && (
        <Cartao>
          <h2 className="font-display mb-1 text-lg font-bold text-titulo">PGMEI</h2>
          <p className="mb-5 text-sm leading-relaxed text-cinza">
            Programa Gerador do DAS do MEI. Informe o CNPJ completo para começar — se você acabou
            de usar a simulação de abertura, ele já vem preenchido.
          </p>
          <div className="max-w-md space-y-4">
            <Campo rotulo="CNPJ completo">
              <input
                className={`${CAMPO_SIM} font-mono`}
                inputMode="numeric"
                maxLength={18}
                value={cnpj}
                onChange={(e) => {
                  setCnpj(mascararCnpj(e.target.value));
                  setErroCnpj(null);
                }}
                placeholder="00.000.000/0000-00"
              />
              {erroCnpj && <p className="mt-1 text-xs text-erro-tinta">{erroCnpj}</p>}
            </Campo>
            <Campo rotulo="Nome empresarial (opcional)">
              <input
                className={CAMPO_SIM}
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="como aparecerá no documento"
              />
            </Campo>
          </div>
          <Navegar
            aoVoltar={null}
            aoAvancar={() => {
              // O CNPJ é conferido de verdade — os dígitos verificadores e tudo.
              // O aluno vai preencher doze períodos depois disto, e descobrir o
              // erro de digitação no fim é perder o preenchimento inteiro.
              if (!cnpjValido(cnpj)) {
                setErroCnpj(
                  "Este CNPJ não é válido: confira os catorze dígitos e os dois dígitos verificadores.",
                );
                return;
              }
              setErroCnpj(null);
              setEtapa(1);
            }}
            rotuloAvancar="Continuar"
          />
        </Cartao>
      )}

      {etapa === 1 && (
        <Cartao>
          <h2 className="font-display mb-4 text-lg font-bold text-titulo">
            Antes de emitir: o que o PGMEI avisa
          </h2>
          <ul className="space-y-3 text-sm leading-relaxed text-tinta">
            <li>
              A contagem da <strong>carência</strong> dos benefícios previdenciários começa a
              partir do <strong>primeiro pagamento em dia</strong>.
            </li>
            <li>
              O MEI, <strong>mesmo sem faturamento</strong>, paga o DAS todo mês. Não existe mês
              isento para MEI ativo.
            </li>
            <li>
              DAS não pago até o vencimento exige emitir um novo, com multa e juros.
            </li>
          </ul>
          <Navegar aoVoltar={() => setEtapa(0)} aoAvancar={() => setEtapa(2)} />
        </Cartao>
      )}

      {etapa === 2 && (
        <Cartao>
          <Caixa>
            O DAS do MEI é composto por INSS (sempre), <strong>ICMS</strong> se a atividade for de
            comércio, indústria ou transporte intermunicipal, e <strong>ISS</strong> se for de
            serviços. Escolha a atividade abaixo para ver a composição mudar.
          </Caixa>

          <fieldset className="mb-5">
            <legend className="mb-2 text-sm font-bold text-titulo">Atividade do MEI</legend>
            <input
              className={CAMPO_SIM}
              type="search"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="cabeleireiro, açougueiro, 4930-2/02…"
            />
            {busca.trim() !== "" && (
              <ul className="mt-1.5 max-h-56 space-y-1 overflow-y-auto rounded-xl border border-borda p-1.5">
                {filtrar(
                  lista.filter((o) => !o.vedada),
                  busca,
                )
                  .slice(0, 20)
                  .map((o) => (
                    <li key={o.id} data-atividade={o.atividade}>
                      <button
                        type="button"
                        onClick={() => {
                          setAtividades((a) =>
                            a.some((x) => x.atividade === o.atividade)
                              ? a
                              : [
                                  ...a,
                                  { atividade: o.atividade, cnae: o.cnae, icms: o.icms, iss: o.iss },
                                ],
                          );
                          setBusca("");
                        }}
                        className="w-full rounded-lg px-2.5 py-2 text-left text-xs transition hover:bg-chip"
                      >
                        <span className="font-semibold text-titulo">{o.atividade}</span>
                        <span className="mt-0.5 block text-cinza">{o.cnae ?? "sem CNAE"}</span>
                      </button>
                    </li>
                  ))}
              </ul>
            )}

            <div className="mt-2 flex flex-wrap gap-2">
              {atividades.length === 0 && (
                <p className="text-xs text-cinza">
                  Nenhuma atividade escolhida — só o INSS entra na composição.
                </p>
              )}
              {atividades.map((a) => (
                <span
                  key={a.atividade}
                  data-escolhida={a.atividade}
                  className="inline-flex items-center gap-1.5 rounded-full bg-chip px-2.5 py-1 text-xs text-chip-tinta"
                >
                  {a.atividade}
                  <button
                    type="button"
                    onClick={() => setAtividades((v) => v.filter((x) => x.atividade !== a.atividade))}
                    aria-label={`Remover ${a.atividade}`}
                    title={`Remover ${a.atividade}`}
                    className="font-bold"
                  >
                    ✕
                  </button>
                </span>
              ))}
            </div>

            <label className="mt-3 flex cursor-pointer items-center gap-2 text-sm text-tinta">
              <input
                type="checkbox"
                className="size-4 accent-laranja"
                checked={caminhoneiro}
                onChange={(e) => setCaminhoneiro(e.target.checked)}
              />
              MEI Caminhoneiro <span className="text-xs text-cinza">(INSS de 12%)</span>
            </label>
          </fieldset>

          <DeOndeVem composicao={composicao} parametros={parametros} />

          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            <Campo rotulo="Ano-calendário">
              <select
                className={CAMPO_SIM}
                value={ano}
                onChange={(e) => {
                  setAno(Number(e.target.value));
                  setMarcados({});
                }}
              >
                {anos.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            </Campo>
            <Campo
              rotulo="Mês de abertura do MEI"
              ajuda="Períodos anteriores à abertura aparecem como indisponíveis: antes daquela data a empresa não existia."
            >
              <select
                className={CAMPO_SIM}
                value={mesAbertura}
                onChange={(e) => setMesAbertura(Number(e.target.value))}
              >
                {MESES.map((m, i) => (
                  <option key={m} value={i + 1}>{m}</option>
                ))}
              </select>
            </Campo>
            <Campo rotulo="Ano de abertura">
              <select
                className={CAMPO_SIM}
                value={anoAbertura}
                onChange={(e) => setAnoAbertura(Number(e.target.value))}
              >
                {anos.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            </Campo>
          </div>

          <h3 className="mt-6 mb-2 text-sm font-bold text-titulo">
            Períodos de apuração de {ano}
          </h3>
          <div className="overflow-x-auto rounded-xl border border-borda">
            <table className="w-full text-xs">
              <caption className="sr-only">
                Períodos de apuração do DAS em {ano}, com o valor e o vencimento de cada mês
              </caption>
              <thead className="bg-superficie-2 text-cinza">
                <tr>
                  <th scope="col" className="p-2 text-left">
                    <label className="flex cursor-pointer items-center gap-1.5">
                      <input
                        type="checkbox"
                        className="size-3.5 accent-laranja"
                        checked={escolhidos.length > 0 && escolhidos.length === periodos.filter((p) => p.podeApurar).length}
                        onChange={(e) => {
                          const todos: Record<number, boolean> = {};
                          if (e.target.checked) {
                            periodos.forEach((p) => {
                              if (p.podeApurar) todos[p.indice] = true;
                            });
                          }
                          setMarcados(todos);
                        }}
                      />
                      <span className="sr-only">Marcar todos os períodos apuráveis</span>
                    </label>
                  </th>
                  <th scope="col" className="p-2 text-left">Período</th>
                  <th scope="col" className="p-2 text-left">Situação</th>
                  <th scope="col" className="p-2 text-right">INSS</th>
                  <th scope="col" className="p-2 text-right">ICMS</th>
                  <th scope="col" className="p-2 text-right">ISS</th>
                  <th scope="col" className="p-2 text-right">Total</th>
                  <th scope="col" className="p-2 text-center">Vencimento</th>
                </tr>
              </thead>
              <tbody>
                {periodos.map((p) => (
                  <tr key={p.indice} data-periodo={`${p.mes}/${ano}`} className="border-t border-borda">
                    <td className="p-2">
                      <input
                        type="checkbox"
                        className="size-3.5 accent-laranja"
                        disabled={!p.podeApurar}
                        checked={!!marcados[p.indice]}
                        onChange={(e) => setMarcados({ ...marcados, [p.indice]: e.target.checked })}
                        aria-label={`Apurar ${p.mes} de ${ano}`}
                      />
                    </td>
                    <td className="p-2 font-medium text-tinta">{p.mes}/{ano}</td>
                    <td className={`p-2 font-semibold ${p.podeApurar ? "text-titulo" : "text-cinza"}`}>
                      {p.podeApurar ? "A vencer" : "Indisponível"}
                    </td>
                    <td className="p-2 text-right text-tinta">{p.podeApurar ? reais(composicao.inss) : "—"}</td>
                    <td className="p-2 text-right text-tinta">{p.podeApurar ? reais(composicao.icms) : "—"}</td>
                    <td className="p-2 text-right text-tinta">{p.podeApurar ? reais(composicao.iss) : "—"}</td>
                    <td className="p-2 text-right font-semibold text-titulo">
                      {p.podeApurar ? reais(p.valor) : "—"}
                    </td>
                    <td className="p-2 text-center text-cinza">{p.podeApurar ? p.vencimento : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 max-w-xs">
            <Campo rotulo="Data para pagamento">
              <input
                className={`${CAMPO_SIM} font-mono`}
                inputMode="numeric"
                maxLength={10}
                value={dataPagamento}
                onChange={(e) => setDataPagamento(mascararData(e.target.value))}
                placeholder="DD/MM/AAAA"
              />
            </Campo>
          </div>

          <p className="mt-3 text-xs text-cinza">
            {escolhidos.length === 0
              ? "Marque ao menos um período para gerar o DAS."
              : `${escolhidos.length} período(s) marcado(s) — total de ${reais(
                  Math.round(escolhidos.length * composicao.total * 100) / 100,
                )}.`}
          </p>

          <Navegar
            aoVoltar={() => setEtapa(1)}
            aoAvancar={gerar}
            rotuloAvancar="Apurar e gerar DAS"
            avancarBloqueado={escolhidos.length === 0}
          />
        </Cartao>
      )}

      {etapa === 3 && (
        <Cartao>
          <p className="mb-4 rounded-xl bg-aviso-fundo px-4 py-3 text-sm font-semibold text-aviso-tinta">
            O documento (DAS) foi gerado.
          </p>

          <div className="overflow-x-auto rounded-xl border border-borda">
            <table className="w-full text-sm">
              <caption className="sr-only">Documentos de arrecadação gerados</caption>
              <thead className="bg-superficie-2 text-xs text-cinza">
                <tr>
                  <th scope="col" className="p-2 text-left">Período</th>
                  <th scope="col" className="p-2 text-left">Número da apuração</th>
                  <th scope="col" className="p-2 text-left">Número do DAS</th>
                  <th scope="col" className="p-2 text-left">Vencimento</th>
                  <th scope="col" className="p-2 text-right">Valor</th>
                </tr>
              </thead>
              <tbody>
                {gerados.map((g) => (
                  <tr key={g.numeroDocumento} className="border-t border-borda">
                    <td className="p-2 text-tinta">{g.periodoApuracao}</td>
                    <td className="p-2 font-mono text-xs text-cinza">{gerarNumeroApuracao()}</td>
                    <td className="p-2 font-mono text-xs text-tinta">{g.numeroDocumento}</td>
                    <td className="p-2 text-tinta">{g.vencimento}</td>
                    <td className="p-2 text-right font-semibold text-titulo">
                      {reais(g.composicao.total)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-5 flex flex-wrap gap-3">
            <button type="button" onClick={imprimir} className="pill-cta text-sm">
              Visualizar e imprimir
            </button>
            <button
              type="button"
              onClick={() => setEtapa(2)}
              className="rounded-full border border-borda px-5 py-2.5 text-sm font-semibold text-titulo transition hover:bg-chip"
            >
              Voltar aos períodos
            </button>
          </div>

          <div className="mt-5 space-y-1.5 text-xs leading-relaxed text-cinza">
            <p>
              Para períodos com informação de benefício previdenciário
              (salário-maternidade, auxílio-doença ou auxílio-reclusão), o valor do INSS é zero.
            </p>
            <p>
              Valores de ICMS e ISS abaixo de R$ 10,00 são acumulados e somados às apurações
              seguintes até atingir o mínimo para recolhimento.
            </p>
            <p>
              No portal real, vários períodos selecionados geram um DAS consolidado. Aqui cada
              período sai numa folha, para você comparar as composições.
            </p>
          </div>

          <AvisoEducacional>
            O documento não tem código de barras nem registro bancário: não pode ser pago.
          </AvisoEducacional>
        </Cartao>
      )}

      {texto && <ExplicacaoDaEtapa texto={texto} />}
      {popup !== null && texto && (
        <PopupDaEtapa texto={texto} passo={etapa} total={4} aoFechar={() => setPopup(null)} />
      )}
    </Moldura>
  );
}

function reais(v: number): string {
  return v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

/**
 * "De onde vem cada valor".
 *
 * O porquê vem de `compor()`, a MESMA função que calculou o número — não é
 * remontado aqui a partir dos fatos. É a lição da 000091: explicação calculada
 * por fora do cálculo passa a contradizer o número, e explicação errada é pior
 * que nenhuma, porque ensina a desconfiar do número certo.
 */
function DeOndeVem({
  composicao,
  parametros,
}: {
  composicao: Composicao;
  parametros: ParametrosDas;
}) {
  const pct = (v: number) => `${(v * 100).toLocaleString("pt-BR", { maximumFractionDigits: 2 })}%`;

  return (
    <section
      role="group"
      aria-labelledby="de-onde-vem"
      id="composicao-do-das"
      className="rounded-xl border border-borda bg-superficie-2 p-4"
    >
      <h3 id="de-onde-vem" className="mb-2 text-sm font-bold text-titulo">
        De onde vem cada valor
      </h3>
      <dl className="space-y-2 text-xs">
        <div>
          <dt className="font-semibold text-tinta">
            INSS — {reais(composicao.inss)}
          </dt>
          <dd className="text-cinza">
            {pct(composicao.baseInss.aliquota)} do salário mínimo de{" "}
            {reais(composicao.baseInss.salario)}
            {composicao.baseInss.caminhoneiro && " (alíquota do MEI caminhoneiro)"}. Todo MEI
            ativo paga, mesmo sem faturamento.
          </dd>
        </div>

        <div>
          <dt className="font-semibold text-tinta">ICMS — {reais(composicao.icms)}</dt>
          <dd className="text-cinza">
            {composicao.porqueIcms.length > 0
              ? `Valor fixo de ${reais(parametros.icms)} porque ${listar(composicao.porqueIcms)} recolhe ICMS.`
              : "Nenhuma atividade escolhida recolhe ICMS."}
          </dd>
        </div>

        <div>
          <dt className="font-semibold text-tinta">ISS — {reais(composicao.iss)}</dt>
          <dd className="text-cinza">
            {composicao.porqueIss.length > 0
              ? `Valor fixo de ${reais(parametros.iss)} porque ${listar(composicao.porqueIss)} recolhe ISS.`
              : "Nenhuma atividade escolhida recolhe ISS."}
          </dd>
        </div>

        <div className="border-t border-borda pt-2">
          <dt className="font-display text-base font-bold text-titulo">
            Total do DAS — {reais(composicao.total)}
          </dt>
          {composicao.semAtividade && (
            <dd className="mt-0.5 text-cinza">
              Sem atividade escolhida, o simulador mostra só o INSS — ele não chuta o tributo do
              seu ramo.
            </dd>
          )}
        </div>
      </dl>
    </section>
  );
}

/** "a" · "a e b" · "a, b e c" — a lista como se escreve em português. */
function listar(nomes: string[]): string {
  if (nomes.length === 1) return nomes[0];
  return `${nomes.slice(0, -1).join(", ")} e ${nomes[nomes.length - 1]}`;
}

function Moldura({
  passos,
  atual,
  heroi,
  identidade,
  children,
}: {
  passos?: string[];
  atual?: number;
  heroi?: { rotulo: string; valor: string; apoio?: string } | null;
  identidade?: [string, string][];
  children: React.ReactNode;
}) {
  return (
    <main className="mx-auto max-w-4xl px-4 py-7 sm:px-6">
      <CabineDaRotina
        rotulo="DAS"
        titulo="Apuração do DAS"
        descricao="O PGMEI: escolha as atividades, marque os períodos e veja o DAS por código de tributo."
        passos={passos}
        atual={atual}
        heroi={heroi}
        identidade={identidade}
      />
      <div className="mt-6">{children}</div>
    </main>
  );
}
